// คืนหอพัก — รายการใบ / เปิดใบใหม่ / ตั้งค่ารายการตรวจ
//
// ใบคืนหอพักคือขั้นตอนที่เจ้าหน้าที่หอพักตรวจของและสภาพห้องก่อนเซ็นให้นักเรียนที่จะออก
// จบการศึกษา หรือย้ายโรงเรียน การเซ็นผ่านเป็นตัวปิดทะเบียนหอ และฝ่ายทะเบียนจำหน่าย
// นักเรียนที่ยังไม่มีใบที่ผ่านการตรวจไม่ได้ (ดู studentExitModel.recordExit)
//
// ตั้งค่ารายการตรวจอยู่เป็นแท็บในหน้านี้ ไม่แยกเป็นเมนู เพราะเป็นงานที่ทำปีละครั้ง
// ตอนหอซื้อของใหม่ ถ้าแยกเมนูจะกินที่ในแถบข้างตลอดเวลาโดยไม่มีใครเปิด
import Header from '../layouts/Header';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table, Tab, Tabs,
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axiosInstance from '../services/axiosInterceptor';
import {
  GROUPS, WITHDRAW_REASONS, TYPE_LABEL, baht, groupLabel, statusBadge, thaiDate, todayISO,
} from './clearanceShared';

const emptyItem = () => ({
  dci_id: null, name: '', group: 'return', required: true, order: 0, active: true, note: '',
});

const genderBadge = (gender) =>
  gender === 'm' ? <Badge bg="primary">ชาย</Badge> : <Badge bg="danger">หญิง</Badge>;

const DormitoryClearance = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  /* ---- รายการใบ ---- */
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ pending: 0, cleared: 0, cancelled: 0 });
  const [statusFilter, setStatusFilter] = useState('pending');
  const [listQuery, setListQuery] = useState('');
  const [listLoading, setListLoading] = useState(false);

  const fetchList = useCallback(async (status, q) => {
    setListLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      const res = await axiosInstance.get(`/api/dormitory/clearance?${params.toString()}`);
      setRows(res.data.data || []);
      setCounts(res.data.counts || { pending: 0, cleared: 0, cancelled: 0 });
    } catch (err) {
      setError(err.response?.data?.message || 'อ่านรายการใบคืนหอพักไม่สำเร็จ');
    } finally {
      setListLoading(false);
    }
  }, []);

  // หน่วงคำค้นไว้ครู่หนึ่ง — ไม่งั้นการพิมพ์เลขที่ใบหนึ่งจะยิงคิวรีทั้งตารางสิบกว่ารอบ
  useEffect(() => {
    const timer = setTimeout(() => fetchList(statusFilter, listQuery), listQuery ? 400 : 0);
    return () => clearTimeout(timer);
  }, [fetchList, statusFilter, listQuery]);

  /* ---- เปิดใบใหม่ ---- */
  const [studentSearch, setStudentSearch] = useState('');
  const [found, setFound] = useState([]);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState(null);
  const [form, setForm] = useState({
    dc_type: 'withdraw', reasonChoice: 'ย้ายสถานศึกษา', reasonOther: '',
    dc_date: todayISO(), dc_note: '',
  });
  const [creating, setCreating] = useState(false);

  const searchStudents = async (key) => {
    setStudentSearch(key);
    if (key.trim().length < 2) { setFound([]); return; }
    setSearching(true);
    try {
      const res = await axiosInstance.get(
        `/api/dormitory/clearance/students/search?q=${encodeURIComponent(key.trim())}`);
      setFound(res.data.data || []);
    } catch {
      setFound([]);
    } finally {
      setSearching(false);
    }
  };

  const createSlip = async (e) => {
    e.preventDefault();
    if (!picked) return;
    setCreating(true);
    setError('');
    try {
      const reason = form.reasonChoice === 'อื่นๆ' ? form.reasonOther.trim() : form.reasonChoice;
      const res = await axiosInstance.post('/api/dormitory/clearance', {
        student_id: picked.st_id,
        dc_type: form.dc_type,
        dc_reason: reason || null,
        dc_date: form.dc_date,
        dc_note: form.dc_note.trim() || null,
      });
      // เปิดใบแล้วพาไปหน้าตรวจทันที เพราะงานยังไม่จบ — ใบเปล่าไม่มีประโยชน์กับใคร
      navigate(`/dormitory/clearance/${res.data.data.dcId}`);
    } catch (err) {
      const data = err.response?.data;
      setError(data?.message || 'เปิดใบคืนหอพักไม่สำเร็จ');
      // มีใบที่ค้างอยู่แล้ว — พาไปใบนั้นให้เลย ไม่ใช่ปล่อยให้ไปหาเองในรายการ
      if (data?.data?.dcId) {
        const go = await Swal.fire({
          icon: 'info',
          title: 'มีใบที่ยังตรวจไม่เสร็จอยู่แล้ว',
          text: data.message,
          showCancelButton: true,
          confirmButtonText: 'เปิดใบนั้น',
          cancelButtonText: 'ปิด',
        });
        if (go.isConfirmed) navigate(`/dormitory/clearance/${data.data.dcId}`);
      }
    } finally {
      setCreating(false);
    }
  };

  /* ---- ตั้งค่ารายการตรวจ ---- */
  const [items, setItems] = useState([]);
  const [itemForm, setItemForm] = useState(emptyItem);
  const [savingItem, setSavingItem] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/api/dormitory/clearance/items?all=1');
      setItems(res.data.data || []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const submitItem = async (e) => {
    e.preventDefault();
    if (!itemForm.name.trim()) return;
    setSavingItem(true);
    setError('');
    try {
      const payload = {
        name: itemForm.name.trim(),
        group: itemForm.group,
        required: itemForm.required ? 1 : 0,
        order: Number(itemForm.order) || 0,
        active: itemForm.active ? 1 : 0,
        note: itemForm.note.trim() || null,
      };
      if (itemForm.dci_id) {
        await axiosInstance.put(`/api/dormitory/clearance/items/${itemForm.dci_id}`, payload);
      } else {
        await axiosInstance.post('/api/dormitory/clearance/items', payload);
      }
      setItemForm(emptyItem());
      setSuccess('บันทึกรายการตรวจแล้ว');
      fetchItems();
    } catch (err) {
      setError(err.response?.data?.message || 'บันทึกรายการตรวจไม่สำเร็จ');
    } finally {
      setSavingItem(false);
    }
  };

  const editItem = (row) => setItemForm({
    dci_id: row.dci_id,
    name: row.dci_name,
    group: row.dci_group,
    required: !!row.dci_required,
    order: row.dci_order,
    active: !!row.dci_active,
    note: row.dci_note || '',
  });

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <Container fluid className="px-0">
          <h2 className="mb-1">คืนหอพัก</h2>
          <p className="text-muted mb-4">
            ตรวจของและสภาพห้องพักก่อนเซ็นให้นักเรียนที่จะออก จบการศึกษา หรือย้ายโรงเรียน —
            เมื่อเซ็นผ่าน ระบบจะย้ายนักเรียนออกจากหอให้ และฝ่ายทะเบียนจะจำหน่ายได้
          </p>

          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

          <Tabs id="dorm-clearance-tabs" activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)} className="mb-4">

            {/* ---------------- รายการใบ ---------------- */}
            <Tab eventKey="list" title={`📋 ใบคืนหอพัก${counts.pending ? ` (${counts.pending})` : ''}`}>
              <Card className="mt-4">
                <Card.Body>
                  <Row className="g-2 mb-3">
                    <Col md={4}>
                      <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <option value="pending">รอตรวจ / ตรวจอยู่ ({counts.pending})</option>
                        <option value="cleared">เซ็นผ่านแล้ว ({counts.cleared})</option>
                        <option value="cancelled">ยกเลิก ({counts.cancelled})</option>
                        <option value="">ทั้งหมด</option>
                      </Form.Select>
                    </Col>
                    <Col md={6}>
                      <Form.Control
                        placeholder="ค้นหาด้วยเลขที่ใบ เลขประจำตัว หรือชื่อนักเรียน"
                        value={listQuery}
                        onChange={(e) => setListQuery(e.target.value)}
                      />
                    </Col>
                    <Col md={2}>
                      <Button variant="outline-secondary" className="w-100"
                        onClick={() => fetchList(statusFilter, listQuery)}>
                        <i className="ri-refresh-line me-1" />โหลดใหม่
                      </Button>
                    </Col>
                  </Row>

                  {listLoading ? (
                    <div className="text-center py-4"><Spinner animation="border" size="sm" /></div>
                  ) : rows.length === 0 ? (
                    <Alert variant="info" className="mb-0">ไม่มีใบคืนหอพักในเงื่อนไขนี้</Alert>
                  ) : (
                    <Table responsive striped hover className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th>เลขที่</th>
                          <th>นักเรียน</th>
                          <th>ชั้น</th>
                          <th>หอพัก</th>
                          <th>ประเภท</th>
                          <th>วันที่คืน</th>
                          <th className="text-end">ค่าเสียหาย</th>
                          <th>สถานะ</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r) => (
                          <tr key={r.dc_id}>
                            <td><strong>{r.dc_no}</strong></td>
                            <td>
                              {r.st_name}
                              <div className="text-muted small">{r.st_idnumber}</div>
                            </td>
                            <td>{r.dc_class || '-'}<div className="text-muted small">{r.dc_year || ''}</div></td>
                            <td>{r.dc_place || '-'}</td>
                            <td>{TYPE_LABEL[r.dc_type] || r.dc_type}
                              <div className="text-muted small">{r.dc_reason || ''}</div></td>
                            <td>{thaiDate(r.dc_date)}</td>
                            <td className="text-end">
                              {Number(r.damage_total) > 0
                                ? <span className="text-danger">{baht(r.damage_total)}</span>
                                : '-'}
                            </td>
                            <td>{statusBadge(r.dc_status)}
                              {r.dc_cleared_by_name && (
                                <div className="text-muted small">{r.dc_cleared_by_name}</div>
                              )}
                            </td>
                            <td className="text-end">
                              <Button size="sm" variant="outline-primary"
                                onClick={() => navigate(`/dormitory/clearance/${r.dc_id}`)}>
                                เปิดใบ
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* ---------------- เปิดใบใหม่ ---------------- */}
            <Tab eventKey="new" title="➕ เปิดใบใหม่">
              <Card className="mt-4">
                <Card.Body>
                  <h5 className="mb-3">ค้นหานักเรียนที่จะคืนหอพัก</h5>
                  <Form.Group className="mb-3">
                    <Form.Label>เลขประจำตัวหรือชื่อนักเรียน</Form.Label>
                    <Form.Control value={studentSearch}
                      onChange={(e) => searchStudents(e.target.value)}
                      placeholder="พิมพ์อย่างน้อย 2 ตัวอักษร" />
                  </Form.Group>

                  {searching && <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>}

                  {found.length > 0 && (
                    <Table responsive striped hover size="sm">
                      <thead className="table-light">
                        <tr>
                          <th>เลขประจำตัว</th><th>ชื่อ</th><th>เพศ</th>
                          <th>ที่พักปัจจุบัน</th><th>ใบที่ค้างอยู่</th><th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {found.map((s) => (
                          <tr key={s.st_id} className={picked?.st_id === s.st_id ? 'table-primary' : ''}>
                            <td>{s.st_idnumber}</td>
                            <td>{s.st_name}</td>
                            <td>{genderBadge(s.st_gen)}</td>
                            <td>
                              {s.dorm_name
                                ? `${s.village_name || ''} · ${s.dorm_name}`
                                : <span className="text-muted">ไม่มีทะเบียนหอพัก</span>}
                            </td>
                            <td>
                              {s.dc_id
                                ? <Badge bg="warning">{s.dc_no}</Badge>
                                : <span className="text-muted">-</span>}
                            </td>
                            <td className="text-end">
                              {s.dc_id ? (
                                <Button size="sm" variant="outline-warning"
                                  onClick={() => navigate(`/dormitory/clearance/${s.dc_id}`)}>
                                  ไปที่ใบเดิม
                                </Button>
                              ) : s.dorm_name ? (
                                <Button size="sm" variant="outline-primary" onClick={() => setPicked(s)}>
                                  เลือก
                                </Button>
                              ) : (
                                <span className="text-muted small">ไม่ต้องออกใบ</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  )}

                  {studentSearch.trim().length >= 2 && !searching && found.length === 0 && (
                    <Alert variant="info">ไม่พบนักเรียนตามคำค้นหา</Alert>
                  )}

                  {picked && (
                    <Form onSubmit={createSlip} className="mt-4 border-top pt-3">
                      <h5 className="mb-3">
                        เปิดใบคืนหอพักของ {picked.st_name} ({picked.st_idnumber})
                        <span className="text-muted fs-6 ms-2">
                          {picked.village_name} · {picked.dorm_name}
                        </span>
                      </h5>
                      <Row className="g-3">
                        <Col md={3}>
                          <Form.Label>ประเภท</Form.Label>
                          <Form.Select value={form.dc_type}
                            onChange={(e) => setForm({ ...form, dc_type: e.target.value })}>
                            <option value="withdraw">จำหน่าย (ออกก่อนจบ / ย้ายโรงเรียน)</option>
                            <option value="graduate">สำเร็จการศึกษา</option>
                          </Form.Select>
                        </Col>
                        <Col md={3}>
                          <Form.Label>สาเหตุ</Form.Label>
                          <Form.Select value={form.reasonChoice}
                            onChange={(e) => setForm({ ...form, reasonChoice: e.target.value })}>
                            {WITHDRAW_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                          </Form.Select>
                        </Col>
                        {form.reasonChoice === 'อื่นๆ' && (
                          <Col md={3}>
                            <Form.Label>ระบุสาเหตุ</Form.Label>
                            <Form.Control value={form.reasonOther}
                              onChange={(e) => setForm({ ...form, reasonOther: e.target.value })} />
                          </Col>
                        )}
                        <Col md={3}>
                          <Form.Label>วันที่คืนหอพัก</Form.Label>
                          <Form.Control type="date" value={form.dc_date}
                            onChange={(e) => setForm({ ...form, dc_date: e.target.value })} required />
                        </Col>
                        <Col md={12}>
                          <Form.Label>หมายเหตุ</Form.Label>
                          <Form.Control as="textarea" rows={2} value={form.dc_note}
                            onChange={(e) => setForm({ ...form, dc_note: e.target.value })} />
                        </Col>
                      </Row>
                      <div className="mt-3 d-flex gap-2">
                        <Button type="submit" variant="primary" disabled={creating}>
                          {creating ? 'กำลังเปิดใบ...' : 'เปิดใบและเริ่มตรวจ'}
                        </Button>
                        <Button variant="light" onClick={() => setPicked(null)}>ยกเลิก</Button>
                      </div>
                      <div className="text-muted small mt-2">
                        รายการที่ต้องตรวจจะถูกคัดลอกลงใบตอนเปิด — แก้รายการแม่บทภายหลังจะไม่กระทบใบที่เปิดไปแล้ว
                      </div>
                    </Form>
                  )}
                </Card.Body>
              </Card>
            </Tab>

            {/* ---------------- ตั้งค่ารายการตรวจ ---------------- */}
            <Tab eventKey="items" title="⚙️ รายการที่ต้องตรวจ">
              <Row className="mt-4 g-3">
                <Col lg={5}>
                  <Card>
                    <Card.Body>
                      <h5 className="mb-3">{itemForm.dci_id ? 'แก้รายการตรวจ' : 'เพิ่มรายการตรวจ'}</h5>
                      <Form onSubmit={submitItem}>
                        <Form.Group className="mb-3">
                          <Form.Label>ชื่อรายการ</Form.Label>
                          <Form.Control value={itemForm.name}
                            onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                            placeholder="เช่น กุญแจห้องพัก" required />
                        </Form.Group>
                        <Row className="g-2 mb-3">
                          <Col sm={7}>
                            <Form.Label>กลุ่ม</Form.Label>
                            <Form.Select value={itemForm.group}
                              onChange={(e) => setItemForm({ ...itemForm, group: e.target.value })}>
                              {GROUPS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
                            </Form.Select>
                          </Col>
                          <Col sm={5}>
                            <Form.Label>ลำดับ</Form.Label>
                            <Form.Control type="number" value={itemForm.order}
                              onChange={(e) => setItemForm({ ...itemForm, order: e.target.value })} />
                          </Col>
                        </Row>
                        <Form.Group className="mb-3">
                          <Form.Label>คำอธิบาย (ไม่บังคับ)</Form.Label>
                          <Form.Control value={itemForm.note}
                            onChange={(e) => setItemForm({ ...itemForm, note: e.target.value })} />
                        </Form.Group>
                        <Form.Check className="mb-2" type="switch" id="dci-required"
                          label="ต้องระบุผลก่อนเซ็นผ่าน"
                          checked={itemForm.required}
                          onChange={(e) => setItemForm({ ...itemForm, required: e.target.checked })} />
                        <Form.Check className="mb-3" type="switch" id="dci-active"
                          label="ใช้งาน (ปิดแล้วจะไม่ถูกใส่ในใบใหม่ แต่ใบเก่ายังอ่านได้)"
                          checked={itemForm.active}
                          onChange={(e) => setItemForm({ ...itemForm, active: e.target.checked })} />
                        <div className="d-flex gap-2">
                          <Button type="submit" variant="primary" disabled={savingItem}>บันทึก</Button>
                          {itemForm.dci_id && (
                            <Button variant="light" onClick={() => setItemForm(emptyItem())}>
                              เพิ่มรายการใหม่
                            </Button>
                          )}
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={7}>
                  <Card>
                    <Card.Body>
                      <h5 className="mb-3">รายการทั้งหมด</h5>
                      <Table responsive striped hover size="sm" className="mb-0">
                        <thead className="table-light">
                          <tr>
                            <th>ลำดับ</th><th>รายการ</th><th>กลุ่ม</th>
                            <th>บังคับ</th><th>ใช้งาน</th><th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((it) => (
                            <tr key={it.dci_id} className={it.dci_active ? '' : 'text-muted'}>
                              <td>{it.dci_order}</td>
                              <td>{it.dci_name}
                                {it.dci_note && <div className="text-muted small">{it.dci_note}</div>}</td>
                              <td>{groupLabel(it.dci_group)}</td>
                              <td>{it.dci_required ? <Badge bg="dark">บังคับ</Badge> : '-'}</td>
                              <td>{it.dci_active
                                ? <Badge bg="success">ใช้งาน</Badge>
                                : <Badge bg="secondary">ปิด</Badge>}</td>
                              <td className="text-end">
                                <Button size="sm" variant="outline-secondary"
                                  onClick={() => editItem(it)}>แก้</Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Tab>
          </Tabs>
        </Container>
      </main>
    </React.Fragment>
  );
};

export default DormitoryClearance;
